import { ReactComponent as CircleCheck } from '@assets/icons/circle-check.svg';
import { ReactComponent as InfoIcon } from '@assets/icons/info-light-circle.svg';
import config from 'config';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';
import parse from 'html-react-parser';

import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useTheme
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import {
  convertDuration,
  formatCurrency,
  getAllowServiceToCheck,
  getTooltipLabelServcie
} from 'utils';

import { ExtendedAddonInterface, ServiceInterface } from 'types';

import { Divider } from 'ui';
import { InfoModal } from '../InfoModal';

const {
  login: { desktop_banners }
} = config;

interface ServiceItemInterface {
  service: ServiceInterface | ExtendedAddonInterface;
  onClick: (service: ServiceInterface | ExtendedAddonInterface) => void;
  isChecked: boolean;
  isAddonVariant: boolean;
  isLastItem: boolean;
}

const ServiceItem = ({
  service,
  onClick,
  isChecked,
  isAddonVariant,
  isLastItem
}: ServiceItemInterface) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: {
            serviceList: { serviceItem }
          }
        }
      }
    }
  } = useTheme();

  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const { guestInfo } = useAppSelector((state) => state.persistGuestAuth);

  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const isGuestLogged = Boolean(guestInfo?.clid) && Boolean(guestInfo?.token);
  const isAuthenticated = isLogged || isGuestLogged;

  const onlineBookingMeth = service.online_booking_meth ?? 0;

  const isAllowToCheck = getAllowServiceToCheck(
    isAuthenticated,
    onlineBookingMeth
  );
  const tooltipLabel = getTooltipLabelServcie(
    isAuthenticated,
    onlineBookingMeth
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const showSvcPrice = useShowSvcPrice();
  const itemPrice = service?.svc_price ?? service?.retail;

  const ButtonIcon = isChecked ? (
    <CircleCheck fill={serviceItem.checkedIconColor} width={25} height={25} />
  ) : (
    <Box
      w='100%'
      h='100%'
      border='1px solid'
      borderColor={serviceItem.uncheckedBorderColor}
      borderRadius='50%'
    />
  );

  const handleClickService = () => {
    if (isAllowToCheck) {
      onClick(service);
    }
  };

  const handleMoreDetailsClick = () => {
    onOpen();
  };

  return (
    <Stack gap={0} w='100%' opacity={isAllowToCheck ? 1 : 0.5}>
      <Grid
        bg={serviceItem.background}
        templateColumns='repeat(8, 1fr) auto'
        templateRows='2'
        rowGap='16px'
        columnGap='15px'
        py='20px'
        px={{ base: '15px', md: '20px' }}
      >
        <GridItem colSpan={8}>
          <Flex
            alignItems='center'
            h='100%'
            gap='15px'
            flexShrink={1}
            onClick={handleClickService}
            cursor='pointer'
          >
            {isAllowToCheck ? (
              <Box w='25px' h='25px' minW='25px' bg='transparent'>
                {ButtonIcon}
              </Box>
            ) : (
              <Tooltip
                label={tooltipLabel}
                placement='bottom-start'
                shouldWrapChildren
                py='15px'
                px='12px'
                width='210px'
                bg='black'
                color='white'
                borderRadius='2px'
                top={-1}
                left={2}
              >
                <Box w='25px' h='25px' minW='25px' bg='transparent'>
                  {ButtonIcon}
                </Box>
              </Tooltip>
            )}

            <Stack gap='5px'>
              <Text
                fontWeight='semibold'
                fontSize={{ base: '18px', md: '22px' }}
              >
                {parse(service?.name ?? '')}
              </Text>
              <Text fontSize='14px' mt='5px'>
                {service?.duration
                  ? convertDuration(service?.duration)
                  : 'No specified'}
                {showSvcPrice && ` | ${formatCurrency(itemPrice)}`}
              </Text>
            </Stack>
          </Flex>
        </GridItem>

        {!isAddonVariant && (
          <GridItem colSpan={1}>
            <Flex
              alignItems='center'
              justifyContent='flex-end'
              h='100%'
              gap='16px'
              mr='-7px'
            >
              <Button
                flexShrink={0}
                variant='text'
                onClick={handleMoreDetailsClick}
                _hover={{
                  '& p, & svg': {
                    color: serviceItem.hoverTextColor,
                    fill: serviceItem.hoverIconColor
                  }
                }}
              >
                <Text
                  fontSize='12px'
                  color={serviceItem.textColor}
                  mr='10px'
                  display={{ base: 'none', sm: 'block' }}
                >
                  More Details
                </Text>
                <InfoIcon width={18} height={18} fill={serviceItem.iconColor} />
              </Button>
            </Flex>
          </GridItem>
        )}

        {service?.dscr && (
          <GridItem colSpan={9}>
            <Text
              fontSize={{ base: '12px', md: '14px' }}
              color='black'
              lineHeight='180%'
            >
              {parse(service?.dscr ?? '')}
            </Text>
          </GridItem>
        )}
      </Grid>

      {!isLastItem && (
        <Divider w='100%' borderColor={serviceItem.dividerColor} />
      )}

      <InfoModal
        isOpen={isOpen}
        onClose={onClose}
        subtitle={service?.name}
        title={`${
          service?.duration
            ? convertDuration(service?.duration)
            : 'No specified'
        }${showSvcPrice ? ` | $${service?.svc_price}` : ''}`}
        description={service?.dscr || ''}
        banners={desktop_banners}
      />
    </Stack>
  );
};

export default ServiceItem;
